/**
 * CR518 Addendum: Shared bbox delta utilities for snippet A/B comparison.
 */

export interface BboxNorm {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BboxDelta {
  iou: number;
  areaMult: number;
  shift: number;
}

export type DeltaClass = "good" | "neutral" | "bad";

export function bboxArea(b: BboxNorm): number {
  return Math.max(0, b.width) * Math.max(0, b.height);
}

function bboxCenter(b: BboxNorm): [number, number] {
  return [b.x + b.width / 2, b.y + b.height / 2];
}

export function bboxIoU(a: BboxNorm, b: BboxNorm): number {
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.width, b.x + b.width);
  const y2 = Math.min(a.y + a.height, b.y + b.height);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const union = bboxArea(a) + bboxArea(b) - inter;
  return union > 0 ? inter / union : 0;
}

export function bboxDelta(a: BboxNorm, b: BboxNorm): BboxDelta {
  const areaA = bboxArea(a);
  const areaB = bboxArea(b);
  const areaMult = areaA > 0 ? areaB / areaA : 0;
  const [cax, cay] = bboxCenter(a);
  const [cbx, cby] = bboxCenter(b);
  const shift = Math.sqrt((cbx - cax) ** 2 + (cby - cay) ** 2);
  return { iou: bboxIoU(a, b), areaMult, shift };
}

export function classifyDelta(d: BboxDelta, cropperUsed?: boolean): DeltaClass {
  if (d.iou < 0.20 || d.areaMult > 2.5 || d.areaMult < 0.25 || d.shift > 0.35) return "bad";
  if (d.iou >= 0.50 && d.areaMult >= 0.60 && d.areaMult <= 1.40) return "good";
  if (cropperUsed && d.iou >= 0.35) return "good";
  return "neutral";
}

/**
 * Safely compute delta between two nullable bboxes.
 * Returns null if either is missing or malformed.
 */
export function safeBboxDelta(a: unknown, b: unknown): BboxDelta | null {
  if (!a || !b || typeof a !== "object" || typeof b !== "object") return null;
  const aa = a as Record<string, unknown>;
  const bb = b as Record<string, unknown>;
  if (typeof aa.x !== "number" || typeof aa.width !== "number") return null;
  if (typeof bb.x !== "number" || typeof bb.width !== "number") return null;
  return bboxDelta(a as BboxNorm, b as BboxNorm);
}
